import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { mcpClassApi } from '@/api/mcpClass.api';
import {
  fromMCPClass,
  isMCPClassFormValid,
  toMCPClass,
  type MCPClassFormState,
} from '@/pages/mcp/mcp-class-form.utils';
import { MCPClassForm } from '@/pages/mcp/MCPClassForm';

export default function EditMCPClassPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MCPClassFormState | null>(null);

  const { data: classData, isLoading } = useQuery({
    queryKey: ['mcp-class', id],
    queryFn: () => mcpClassApi.getMCPClass(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (classData) setForm(fromMCPClass(classData));
  }, [classData]);

  const updateMutation = useMutation({
    mutationFn: (f: MCPClassFormState) => mcpClassApi.updateMCPClass(f.id!, toMCPClass(f)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-class', id] });
      queryClient.invalidateQueries({ queryKey: ['mcp-classes'] });
      navigate(`/plugins/mcps/classes/${id}`);
    },
  });

  if (isLoading || !form) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/classes/${id}`)}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">编辑 MCP 模版</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{form.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/plugins/mcps/classes/${id}`)}>
            取消
          </Button>
          <Button
            disabled={!isMCPClassFormValid(form) || updateMutation.isPending}
            onClick={() => updateMutation.mutate(form)}
          >
            {updateMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}保存修改
          </Button>
        </div>
      </div>

      <MCPClassForm form={form} onChange={setForm} />
    </div>
  );
}
